<template>
  <div class="opportunity-details-view">
    <woot-loading-state
      v-if="isLoading"
      :message="$t('OPPORTUNITY_DETAILS.TITLE')"
    />

    <empty-state
      v-else-if="!opportunity"
      :title="$t('OPPORTUNITY_DETAILS.EMPTY.TITLE')"
      :message="$t('OPPORTUNITY_DETAILS.EMPTY.MESSAGE')"
    />

    <template v-else>
      <div
        v-if="statusBanner"
        class="status-banner"
        :class="`status-banner--${statusBanner.variant}`"
      >
        <span class="status-banner-icon">{{ statusBanner.icon }}</span>
        <div class="status-banner-text">
          <p class="status-banner-title">{{ statusBanner.title }}</p>
          <p v-if="statusBanner.subtitle" class="status-banner-subtitle">
            {{ statusBanner.subtitle }}
          </p>
        </div>
      </div>

      <div class="timeline-card">
        <p class="section-title">
          {{ $t('OPPORTUNITY_DETAILS.PIPELINE_PROGRESS') }}
        </p>
        <div class="timeline-track">
          <div
            v-for="(stage, index) in activeTimelineStages"
            :key="stage.id"
            class="timeline-step"
            :class="{
              'completed-step':
                isCompletedByIndex(stage) || isActiveStage(stage),
            }"
          >
            <span class="dot" :class="stageDotClass(stage)">
              {{ index + 1 }}
            </span>
            <span class="step-name">{{ stage.name }}</span>
          </div>
        </div>
      </div>

      <div class="content-grid">
        <section class="card notes-history-card">
          <opportunity-notes
            embedded
            :notes="notes"
            :is-locked="isOpportunityLocked"
            :is-submitting="isSubmittingNote"
            @add-note="submitNote"
          />

          <opportunity-history
            embedded
            class="notes-history-card__history"
            :histories="histories"
            :opportunity="opportunity"
          />
        </section>

        <opportunity-activities
          class="details-activities"
          :opportunity="opportunity"
          :is-locked="isOpportunityLocked"
          @activities-changed="refreshHistoriesSilently"
        />

        <opportunity-details-card
          :opportunity="opportunity"
          :products="products"
          :can-mark-won="canMarkWon"
          :can-mark-lost="canMarkLost"
          :can-cancel="canCancelOpportunity"
          :is-locked="isOpportunityLocked"
          :pipeline-users="pipelineUsers"
          :negotiation-form-enabled="negotiationFormEnabled"
          @open-status-modal="openStatusModal"
          @open-edit-modal="openEditModal"
          @refresh-products="loadRelations"
          @owner-changed="refreshOpportunitySilently"
          @temperature-changed="onTemperatureChanged"
          @open-negotiation-form="openNegotiationForm"
        />
      </div>
    </template>

    <opportunity-negotiation-drawer
      v-if="opportunity"
      :show="showNegotiationForm"
      :opportunity="opportunity"
      :is-locked="isOpportunityLocked"
      :pending-question-ids="negotiationPendingQuestionIds"
      :target-stage="negotiationTargetStage"
      @saved="onNegotiationFormSaved"
      @close="closeNegotiationForm"
    />

    <opportunity-modals
      :opportunity="opportunity"
      :show-status-dialog="showStatusDialog"
      :show-edit-dialog="showEditDialog"
      :status-action="statusAction"
      :active-timeline-stages="activeTimelineStages"
      :active-loss-reasons="activeLossReasons"
      :is-submitting-status="isSubmittingStatus"
      :is-submitting-edit="isSubmittingEdit"
      @close-status="closeStatusModal"
      @close-edit="closeEditModal"
      @submit-status="submitStatusChange"
      @submit-edit="submitEdit"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import EmptyState from '../../../../components/widgets/EmptyState';
import { sortStagesByPosition } from '../../../../helper/opportunityPipelineHelper';
import OpportunityNotes from './OpportunityNotes.vue';
import OpportunityDetailsCard from './OpportunityDetailsCard.vue';
import OpportunityModals from './OpportunityModals.vue';
import OpportunityHistory from './OpportunityHistory.vue';
import OpportunityActivities from './activities/OpportunityActivities.vue';
import OpportunityNegotiationDrawer from './negotiationForm/OpportunityNegotiationDrawer.vue';
import {
  hasActiveNegotiationForm,
  pendingQuestionIdsFromError,
} from '../../../../helper/opportunityNegotiationFormHelper';

export default {
  name: 'OpportunityDetailsView',
  components: {
    EmptyState,
    OpportunityNotes,
    OpportunityDetailsCard,
    OpportunityModals,
    OpportunityHistory,
    OpportunityActivities,
    OpportunityNegotiationDrawer,
  },
  mixins: [alertMixin],
  props: {
    opportunityId: {
      type: [String, Number],
      default: null,
    },
    conversationId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isLoading: !!(
        this.opportunityId ||
        this.conversationId ||
        this.$route.query.opportunity_id ||
        this.$route.query.conversation_id
      ),
      isSubmittingNote: false,
      isSubmittingStatus: false,
      isSubmittingEdit: false,
      opportunity: null,
      histories: [],
      notes: [],
      products: [],
      timelineStages: [],
      lossReasons: [],
      pipelineStatus: 'active',
      pipelineUserIds: [],
      pipelineMetaTimer: null,
      showStatusDialog: false,
      showEditDialog: false,
      statusAction: 'won',
      negotiationFormEnabled: false,
      showNegotiationForm: false,
      negotiationPendingQuestionIds: [],
      negotiationTargetStage: null,
      negotiationRetry: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      agents: 'agents/getAgents',
    }),
    pipelineUsers() {
      return this.agents.filter(agent =>
        this.pipelineUserIds.includes(agent.id)
      );
    },
    activeLossReasons() {
      return this.lossReasons.filter(reason => reason.status !== 'inactive');
    },
    activeTimelineStages() {
      return this.timelineStages.filter(
        stage => stage.status !== 'inactive' || this.isCurrentStage(stage)
      );
    },
    isPipelineInactive() {
      return this.pipelineStatus === 'inactive';
    },
    isOpportunityLocked() {
      return (
        !!this.opportunity &&
        (this.opportunity.status !== 'open' || this.isPipelineInactive)
      );
    },
    canMarkWon() {
      return !!this.opportunity && !this.isOpportunityLocked;
    },
    canMarkLost() {
      return !!this.opportunity && !this.isOpportunityLocked;
    },
    canCancelOpportunity() {
      return (
        !!this.opportunity &&
        this.opportunity.status !== 'canceled' &&
        !this.isPipelineInactive
      );
    },
    statusBanner() {
      if (!this.opportunity) {
        return null;
      }

      if (this.opportunity.status === 'canceled') {
        return {
          variant: 'canceled',
          icon: '🚫',
          title: this.$t('OPPORTUNITY_DETAILS.STATUS_BANNER.CANCELED_TITLE'),
          subtitle: this.$t(
            'OPPORTUNITY_DETAILS.STATUS_BANNER.CANCELED_SUBTITLE'
          ),
        };
      }

      if (this.opportunity.status === 'won') {
        return {
          variant: 'won',
          icon: '🏆',
          title: this.$t('OPPORTUNITY_DETAILS.STATUS_BANNER.WON_TITLE'),
          subtitle: this.$t('OPPORTUNITY_DETAILS.STATUS_BANNER.WON_SUBTITLE'),
        };
      }

      if (this.opportunity.status === 'lost') {
        return {
          variant: 'lost',
          icon: '❌',
          title: this.$t('OPPORTUNITY_DETAILS.STATUS_BANNER.LOST_TITLE'),
          subtitle:
            (this.opportunity.loss_reason &&
              this.opportunity.loss_reason.name &&
              `${this.$t(
                'OPPORTUNITY_DETAILS.STATUS_BANNER.LOST_SUBTITLE_PREFIX'
              )} ${this.opportunity.loss_reason.name}`) ||
            null,
        };
      }

      if (this.isPipelineInactive) {
        return {
          variant: 'locked',
          icon: '🔒',
          title: this.$t('OPPORTUNITY_DETAILS.STATUS_BANNER.LOCKED_TITLE'),
          subtitle: this.$t(
            'OPPORTUNITY_DETAILS.STATUS_BANNER.LOCKED_SUBTITLE'
          ),
        };
      }

      return null;
    },
  },
  watch: {
    '$route.fullPath': {
      handler() {
        this.fetchData();
      },
    },
  },
  mounted() {
    this.fetchData();
    if (!this.agents.length) {
      this.$store.dispatch('agents/get');
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.pipelineMetaTimer = window.setInterval(
      this.refreshPipelineMetaSilently,
      20000
    );
    window.bus.$on('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  beforeDestroy() {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    if (this.pipelineMetaTimer) {
      window.clearInterval(this.pipelineMetaTimer);
    }
    window.bus.$off('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      this.opportunity = null;
      this.closeNegotiationForm();
      this.closeStatusModal();
      this.closeEditModal();

      try {
        const selectedOpportunityId =
          this.opportunityId || this.$route.query.opportunity_id;
        const selectedConversationId =
          this.conversationId || this.$route.query.conversation_id;

        if (selectedConversationId) {
          await this.fetchOpportunityByConversation(
            selectedConversationId,
            selectedOpportunityId
          );
        } else if (selectedOpportunityId) {
          await this.fetchOpportunity(selectedOpportunityId);
        }

        if (!this.opportunity) {
          return;
        }

        await this.loadRelations();
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_DETAILS.ERRORS.LOAD'));
      } finally {
        this.isLoading = false;
      }
    },
    async fetchOpportunityByConversation(
      conversationId,
      preferredOpportunityId
    ) {
      const items =
        (await this.$store.dispatch('opportunities/list', {
          conversation_id: conversationId,
        })) || [];

      if (!items.length) {
        if (preferredOpportunityId) {
          await this.fetchOpportunity(preferredOpportunityId);
        }
        return;
      }

      const matchedOpportunity = preferredOpportunityId
        ? items.find(item => String(item.id) === String(preferredOpportunityId))
        : null;

      this.opportunity = matchedOpportunity || items[0];

      if (this.$route.params.opportunityId !== String(this.opportunity.id)) {
        this.$router.replace({
          name: 'opportunities_dashboard',
          params: {
            accountId: this.$route.params.accountId,
            opportunityId: this.opportunity.id,
          },
          query: {
            ...this.$route.query,
            conversation_id: String(conversationId),
          },
        });
      }
    },
    isCurrentOpportunity(opportunityId) {
      return (
        !!this.opportunity &&
        Number(this.opportunity.id) === Number(opportunityId)
      );
    },
    async fetchOpportunity(opportunityId) {
      this.opportunity = await this.$store.dispatch(
        'opportunities/getById',
        opportunityId
      );
      this.$store.dispatch('opportunities/refreshIfCurrent', this.opportunity);
    },
    async loadRelations() {
      const opportunityId = this.opportunity.id;
      const pipelineId = this.opportunity.pipeline_id;

      const [relations, pipelineMeta] = await Promise.allSettled([
        Promise.all([
          this.$store.dispatch('opportunities/getHistories', opportunityId),
          this.$store.dispatch('opportunities/getNotes', opportunityId),
          this.$store.dispatch('opportunities/getProducts', opportunityId),
        ]),
        this.fetchPipelineMeta(pipelineId),
      ]);

      if (relations.status === 'rejected') throw relations.reason;

      const [histories, notes, products] = relations.value;
      this.histories = histories || [];
      this.notes = notes || [];
      this.products = products || [];

      if (pipelineMeta.status === 'rejected') throw pipelineMeta.reason;
    },
    async fetchPipelineMeta(pipelineId) {
      const [pipeline, stages, reasons] = await Promise.all([
        this.$store.dispatch('opportunityPipelines/show', pipelineId),
        this.$store.dispatch('opportunityPipelines/getStages', pipelineId),
        this.$store.dispatch('opportunityPipelines/getLossReasons', pipelineId),
      ]);

      this.pipelineStatus = pipeline.status;
      this.pipelineUserIds = pipeline.user_ids || [];
      this.negotiationFormEnabled = hasActiveNegotiationForm(pipeline);
      this.timelineStages = sortStagesByPosition(stages || []);
      this.lossReasons = reasons || [];
    },
    async refreshPipelineMetaSilently() {
      if (!(this.opportunity && this.opportunity.pipeline_id)) {
        return;
      }

      try {
        await this.fetchPipelineMeta(this.opportunity.pipeline_id);
      } catch (error) {
        // Background sync failure is non-critical, keep previously loaded data.
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.refreshPipelineMetaSilently();
      }
    },
    handleOpportunityUpdatedEvent(data) {
      if (
        !this.opportunity ||
        Number(data?.id) !== Number(this.opportunity.id)
      ) {
        return Promise.resolve();
      }
      return this.refreshOpportunitySilently();
    },
    async refreshOpportunitySilently() {
      try {
        const [opportunity, histories] = await Promise.all([
          this.$store.dispatch('opportunities/getById', this.opportunity.id),
          this.$store.dispatch(
            'opportunities/getHistories',
            this.opportunity.id
          ),
        ]);
        this.opportunity = opportunity;
        this.histories = histories || [];
        this.$store.dispatch('opportunities/refreshIfCurrent', opportunity);
      } catch (error) {
        // Background sync failure is non-critical, keep previously loaded data.
      }
    },
    async refreshHistoriesSilently() {
      if (!(this.opportunity && this.opportunity.id)) {
        return;
      }

      const histories = await this.$store
        .dispatch('opportunities/getHistories', this.opportunity.id)
        .catch(() => null);
      if (histories) {
        this.histories = histories;
      }
    },
    openNegotiationForm(
      pendingQuestionIds = [],
      targetStage = null,
      retry = null
    ) {
      this.negotiationPendingQuestionIds = pendingQuestionIds;
      this.negotiationTargetStage = targetStage;
      this.negotiationRetry = retry;
      this.showNegotiationForm = true;
    },
    closeNegotiationForm() {
      this.showNegotiationForm = false;
      this.negotiationPendingQuestionIds = [];
      this.negotiationTargetStage = null;
      this.negotiationRetry = null;
    },
    findTimelineStage(stageId) {
      return (
        this.timelineStages.find(
          stage => Number(stage.id) === Number(stageId)
        ) || null
      );
    },
    async openNegotiationFormIfPending(targetStageId, retry) {
      const { opportunity } = this;
      const targetStage = this.findTimelineStage(targetStageId);
      if (!this.negotiationFormEnabled || !targetStage) {
        return false;
      }

      let pendingQuestionIds;
      try {
        pendingQuestionIds = await this.$store.dispatch(
          'opportunities/pendingNegotiationQuestionIds',
          { opportunity, stage: targetStage }
        );
      } catch (error) {
        return false;
      }
      if (!pendingQuestionIds || !pendingQuestionIds.length) {
        return false;
      }
      if (this.isCurrentOpportunity(opportunity.id)) {
        this.openNegotiationForm(pendingQuestionIds, targetStage, retry);
      }
      return true;
    },
    openNegotiationFormForPending(error, targetStageId, retry) {
      const pendingQuestionIds = pendingQuestionIdsFromError(error);
      if (!this.negotiationFormEnabled || !pendingQuestionIds.length) {
        return false;
      }
      this.openNegotiationForm(
        pendingQuestionIds,
        this.findTimelineStage(targetStageId),
        retry
      );
      return true;
    },
    async onNegotiationFormSaved(opportunity) {
      const retry = this.negotiationRetry;
      if (opportunity) {
        this.opportunity = opportunity;
      }
      this.closeNegotiationForm();
      await this.refreshHistoriesSilently();
      if (retry) {
        await retry();
      }
    },
    onTemperatureChanged(opportunity) {
      if (opportunity) {
        this.opportunity = opportunity;
      }
      return this.refreshOpportunitySilently();
    },
    stageIndex(stage) {
      return this.activeTimelineStages.findIndex(
        item => Number(item.id) === Number(stage.id)
      );
    },
    effectiveCurrentStageIndex() {
      const index = this.activeTimelineStages.findIndex(item =>
        this.isCurrentStage(item)
      );
      return index >= 0 ? index : 0;
    },
    currentStageIndex() {
      return this.effectiveCurrentStageIndex();
    },
    isNextStage(stage) {
      const current = this.currentStageIndex();
      const index = this.stageIndex(stage);

      return current >= 0 && index === current + 1;
    },
    isActiveStage(stage) {
      const current = this.currentStageIndex();
      const index = this.stageIndex(stage);
      return current >= 0 && index === current;
    },
    isCompletedByIndex(stage) {
      const current = this.currentStageIndex();
      const index = this.stageIndex(stage);
      return current >= 0 && index >= 0 && index < current;
    },
    stageDotClass(stage) {
      const current = this.isActiveStage(stage);
      const next = this.isNextStage(stage);

      return {
        active: current,
        completed: !current && this.isCompletedByIndex(stage),
        next: !current && next,
      };
    },
    isCurrentStage(stage) {
      return (
        Number(stage.id) ===
        Number(this.opportunity && this.opportunity.pipeline_stage_id)
      );
    },
    async submitNote(content, onFailure) {
      if (
        !content ||
        !(this.opportunity && this.opportunity.id) ||
        this.isOpportunityLocked
      ) {
        return;
      }

      this.isSubmittingNote = true;
      try {
        await this.$store.dispatch('opportunities/createNote', {
          opportunityId: this.opportunity.id,
          content,
        });
        const [notes, histories] = await Promise.all([
          this.$store.dispatch('opportunities/getNotes', this.opportunity.id),
          this.$store.dispatch(
            'opportunities/getHistories',
            this.opportunity.id
          ),
        ]);
        this.notes = notes || [];
        this.histories = histories || [];
      } catch (error) {
        if (onFailure) onFailure();
        this.showAlert(this.$t('OPPORTUNITY_DETAILS.ERRORS.ADD_NOTE'));
      } finally {
        this.isSubmittingNote = false;
      }
    },
    openStatusModal(action) {
      const isActionAllowed = {
        won: this.canMarkWon,
        lost: this.canMarkLost,
        canceled: this.canCancelOpportunity,
      }[action];

      if (!isActionAllowed) {
        return;
      }

      this.statusAction = action;
      this.showStatusDialog = true;
    },
    closeStatusModal() {
      this.showStatusDialog = false;
    },
    async submitStatusChange(statusForm, { checkNegotiationForm = true } = {}) {
      if (!(this.opportunity && this.opportunity.id)) {
        return;
      }

      const { opportunity } = this;
      const submitAfterForm = () =>
        this.isCurrentOpportunity(opportunity.id) &&
        this.submitStatusChange(statusForm, { checkNegotiationForm: false });
      this.isSubmittingStatus = true;

      try {
        const selectedStageId = statusForm.pipelineStageId;
        if (
          selectedStageId &&
          Number(selectedStageId) !== Number(opportunity.pipeline_stage_id)
        ) {
          if (
            checkNegotiationForm &&
            (await this.openNegotiationFormIfPending(
              selectedStageId,
              submitAfterForm
            ))
          ) {
            return;
          }
          await this.$store.dispatch('opportunities/moveStage', {
            opportunityId: opportunity.id,
            pipelineStageId: selectedStageId,
          });
        }

        if (statusForm.note && statusForm.note.trim()) {
          await this.$store.dispatch('opportunities/createNote', {
            opportunityId: opportunity.id,
            content: statusForm.note.trim(),
          });
        }

        await this.$store.dispatch('opportunities/updateStatus', {
          opportunityId: opportunity.id,
          status: this.statusAction,
          lossReasonId:
            this.statusAction === 'lost' ? statusForm.lossReasonId : null,
        });

        if (!this.isCurrentOpportunity(opportunity.id)) return;
        await this.fetchOpportunity(opportunity.id);
        await this.loadRelations();
        this.closeStatusModal();
      } catch (error) {
        const backendMessage =
          error && error.response && error.response.data
            ? error.response.data.message
            : null;
        this.showAlert(
          backendMessage || this.$t('OPPORTUNITY_DETAILS.ERRORS.STATUS')
        );
        if (this.isCurrentOpportunity(opportunity.id)) {
          this.openNegotiationFormForPending(
            error,
            statusForm.pipelineStageId,
            submitAfterForm
          );
        }
      } finally {
        this.isSubmittingStatus = false;
      }
    },
    openEditModal() {
      if (this.isOpportunityLocked) {
        return;
      }

      this.showEditDialog = true;
    },
    closeEditModal() {
      this.showEditDialog = false;
    },
    async submitEdit(editForm, { checkNegotiationForm = true } = {}) {
      if (!(this.opportunity && this.opportunity.id)) {
        return;
      }

      const { opportunity } = this;
      const submitAfterForm = () =>
        this.isCurrentOpportunity(opportunity.id) &&
        this.submitEdit(editForm, { checkNegotiationForm: false });
      this.isSubmittingEdit = true;
      try {
        const changesStage =
          !!editForm.pipelineStageId &&
          Number(editForm.pipelineStageId) !==
            Number(opportunity.pipeline_stage_id);
        if (
          changesStage &&
          checkNegotiationForm &&
          (await this.openNegotiationFormIfPending(
            editForm.pipelineStageId,
            submitAfterForm
          ))
        ) {
          return;
        }

        await this.$store.dispatch('opportunities/updateById', {
          opportunityId: opportunity.id,
          payload: {
            name: editForm.name,
            description: editForm.description,
            value: editForm.value,
            currency: opportunity.currency,
            pipeline_stage_id: changesStage ? editForm.pipelineStageId : null,
          },
        });

        if (!this.isCurrentOpportunity(opportunity.id)) return;
        await this.fetchOpportunity(opportunity.id);
        await this.loadRelations();
        this.closeEditModal();
      } catch (error) {
        const backendMessage =
          error && error.response && error.response.data
            ? error.response.data.message
            : null;
        this.showAlert(
          backendMessage || this.$t('OPPORTUNITY_DETAILS.ERRORS.UPDATE')
        );
        if (this.isCurrentOpportunity(opportunity.id)) {
          this.openNegotiationFormForPending(
            error,
            editForm.pipelineStageId,
            submitAfterForm
          );
        }
      } finally {
        this.isSubmittingEdit = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.opportunity-details-view {
  background: var(--s-50);
  box-sizing: border-box;
  min-height: calc(100vh - 9rem);
  overflow-x: hidden;
  padding: var(--space-medium);
  width: 100%;
}

.status-banner {
  align-items: flex-start;
  border: 1px solid transparent;
  border-radius: var(--border-radius-medium);
  display: flex;
  gap: var(--space-normal);
  margin-bottom: var(--space-medium);
  padding: var(--space-normal) var(--space-medium);
}

.status-banner-icon {
  font-size: 1.75rem;
  line-height: 1;
}

.status-banner-title {
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.status-banner-subtitle {
  font-size: var(--font-size-small);
  margin: var(--space-micro) 0 0;
}

.status-banner--won {
  background: var(--g-50);
  border-color: var(--g-200);
  color: var(--g-800);
}

.status-banner--lost {
  background: var(--r-50);
  border-color: var(--r-200);
  color: var(--r-800);
}

.status-banner--canceled {
  background: var(--s-100);
  border-color: var(--s-200);
  color: var(--s-800);
}

.status-banner--locked {
  background: var(--y-50);
  border-color: var(--y-200);
  color: var(--y-800);
}

.timeline-card,
.card {
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-medium);
  margin-bottom: var(--space-medium);
  padding: var(--space-medium);
}

.timeline-card {
  padding: var(--space-normal) var(--space-medium) var(--space-large);
}

.section-title {
  color: var(--s-900);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-normal);
}

.timeline-track {
  display: flex;
  gap: 0;
  overflow-x: auto;
  padding: var(--space-small) var(--space-small) var(--space-micro);
}

.timeline-step {
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1 0 12rem;
  gap: var(--space-small);
  min-width: 12rem;
  position: relative;
}

.timeline-step:first-child {
  margin-left: var(--space-smaller);
}

.timeline-step::before {
  background: #e5e7eb;
  content: '';
  height: 4px;
  left: -50%;
  position: absolute;
  top: 1.35rem;
  width: 100%;
}

.timeline-step.completed-step::before {
  background: #2563eb;
}

.timeline-step:first-child::before {
  display: none;
}

.dot {
  align-items: center;
  background: #e5e7eb;
  border: 0;
  border-radius: 50%;
  color: #6b7280;
  display: inline-flex;
  font-size: 1.125rem;
  font-weight: 700;
  height: 3rem;
  justify-content: center;
  line-height: 1;
  width: 3rem;
  z-index: 1;
}

.dot.active {
  background: #22c55e;
  border: 0;
  box-shadow: none;
  color: #fff;
}

.dot.completed {
  background: #2563eb;
  color: #fff;
}

.dot.next {
  background: var(--white);
  border: 2px solid #e5e7eb;
  color: #6b7280;
}

.step-name {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin-top: 0.1rem;
  text-align: center;
}

.content-grid {
  display: grid;
  gap: var(--space-medium);
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.55fr) minmax(0, 1.1fr);
  width: 100%;
}

.content-grid > .card {
  min-width: 0;
}

.notes-history-card {
  display: flex;
  flex-direction: column;
}

.notes-history-card__history {
  border-top: 1px solid var(--s-100);
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  margin-top: var(--space-medium);
  padding-top: var(--space-medium);
}

@media (min-width: 1201px) {
  .notes-history-card ::v-deep .notes-list {
    max-height: 24rem;
  }

  .notes-history-card__history ::v-deep .history-list {
    flex: 1 1 auto;
    height: 0;
    max-height: none;
    min-height: 16rem;
  }

  .details-activities {
    min-height: 48rem;

    ::v-deep .activities-list {
      flex: 1 1 auto;
      height: 0;
      max-height: none;
      min-height: 12rem;
    }
  }
}

@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .timeline-step {
    flex: 1 0 10rem;
    min-width: 10rem;
  }
}
</style>
